/**
 * @file index.tsx
 *
 * @fileoverview Renders the Meta tracking pixel script in the page. By default tracks only page views and integrates with CMP (if enabled).
 */

import React, { useContext, useEffect, useRef } from 'react';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import Script from 'next/script';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

declare global {
  var fbq: any;
}

const usePageReloadIfMetaPixelConsentGotRemoved = (currentMetaPixelConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise MetaPixel does not get excluded.
  const previousMetaPixelConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousMetaPixelConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.MetaPixel,
    );
  }, [previousMetaPixelConsent, userContext?.consents]);

  // Compare if consent has changed from true to false.
  if (previousMetaPixelConsent.current && !currentMetaPixelConsent) {
    Router.reload();
  }
};

export const MetaPixel = () => {
  const userContext: UserContextInterface = useContext(UserContext);
  const { APP } = useFrontendConfig();
  const testing = process.env.testing;
  const staging = process.env.staging;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.MetaPixel,
  );

  usePageReloadIfMetaPixelConsentGotRemoved(isUserConsentNeededAndGiven);

  return APP.META_PIXEL_ID &&
    isUserConsentNeededAndGiven &&
    !testing &&
    !staging ? (
    <>
      <Script id="MetaPixel-script" type="text/javascript" async={true}>
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${APP.META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${APP.META_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  ) : null;
};

export default MetaPixel;
